/**
 * Email template utility functions
 * These helpers assist with email template rendering and variable replacement
 */

import { userRegistrationTemplate, passwordResetTemplate, notificationTemplate, reportSendingTemplate, generateEventListHtml } from '../templates/emailTemplates';

type TemplateVariables = Record<string, string | number | boolean>;

/**
 * Replace template variables in an email template
 * Uses Handlebars-style syntax {{variable}} which is compatible with SendGrid templates
 * @param template - The email template string with {{variable}} placeholders
 * @param variables - Object containing variable names and values
 * @returns Processed template with variables replaced
 */
export const replaceTemplateVariables = (
  template: string,
  variables: TemplateVariables
): string => {
  let processedTemplate = template;

  // Replace each variable placeholder with its value
  Object.entries(variables).forEach(([key, value]) => {
    const placeholder = new RegExp(`{{${key}}}`, 'g');
    processedTemplate = processedTemplate.replace(placeholder, String(value));
  });

  return processedTemplate;
};

/**
 * Generate unsubscribe URL with tracking parameters
 * @param userId - User ID for the unsubscribe link
 * @param emailType - Type of email being sent
 * @returns Complete unsubscribe URL
 */
export const generateUnsubscribeUrl = (
  userId: string,
  emailType: 'notification' | 'marketing' | 'transactional'
): string => {
  const baseUrl = 'https://medihub.com/unsubscribe';
  const token = Buffer.from(`${userId}:${emailType}:${Date.now()}`).toString('base64');
  
  return `${baseUrl}?token=${token}`;
};

/**
 * Generate tracking pixel URL for email open tracking
 * @param emailId - ID of the email being sent
 * @param userId - ID of the user receiving the email
 * @returns Tracking pixel URL
 */
export const generateTrackingPixel = (
  emailId: string,
  userId: string
): string => {
  const baseUrl = 'https://track.medihub.com/pixel.png';
  const params = new URLSearchParams({
    eid: emailId,
    uid: userId,
    t: Date.now().toString()
  });
  
  return `${baseUrl}?${params.toString()}`;
};

/**
 * Format date for email templates in Hungarian format
 * @param date - Date to format
 * @param includeTime - Whether to include time
 * @returns Formatted date string
 */
export const formatEmailDate = (
  date: Date, 
  includeTime: boolean = false
): string => {
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    ...(includeTime ? { hour: '2-digit', minute: '2-digit' } : {})
  };
  
  return date.toLocaleDateString('hu-HU', options);
};

/**
 * Generate email preview text
 * @param template - Full email template
 * @param maxLength - Maximum length of preview
 * @returns Email preview text
 */
export const generateEmailPreview = (
  template: string,
  maxLength: number = 100
): string => {
  // Extract text content from HTML
  const textContent = template
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  
  // Limit to maxLength characters
  return textContent.length > maxLength
    ? `${textContent.substring(0, maxLength)}...`
    : textContent;
};

/**
 * Get the HTML template for user registration emails
 * @param variables - Variables to replace in the template
 * @returns HTML template with variables replaced
 */
export const getUserRegistrationEmailTemplate = (variables: TemplateVariables): string => {
  return replaceTemplateVariables(userRegistrationTemplate, variables);
};

/**
 * Get the HTML template for password reset emails
 * @param variables - Variables to replace in the template
 * @returns HTML template with variables replaced
 */
export const getPasswordResetEmailTemplate = (variables: TemplateVariables): string => {
  return replaceTemplateVariables(passwordResetTemplate, variables);
};

/**
 * Get the HTML template for notification emails with events
 * @param variables - Variables to replace in the template
 * @param events - Regular events to include
 * @param recommendedEvents - Recommended events to include
 * @returns HTML template with variables and events replaced
 */
export const getNotificationEmailTemplate = (
  variables: TemplateVariables,
  events: Array<{name: string, type: string, date: string, location: string, link: string}>,
  recommendedEvents: Array<{name: string, type: string, date: string, location: string, link: string}> = []
): string => {
  // Generate HTML for event list
  const eventListHtml = generateEventListHtml(events, recommendedEvents);
  
  // Add event list to variables
  const allVariables = {
    ...variables,
    eventList: eventListHtml
  };
  
  return replaceTemplateVariables(notificationTemplate, allVariables);
};

/**
 * Get the HTML template for report sending emails
 * @param variables - Variables to replace in the template
 * @returns HTML template with variables replaced
 */
export const getReportEmailTemplate = (variables: TemplateVariables): string => {
  return replaceTemplateVariables(reportSendingTemplate, variables);
};

/**
 * MediHub to SendGrid template variables mapping
 * Maps our internal variable names to SendGrid dynamic template syntax
 */
export const sendGridVariableMapping = {
  // Common variables
  name: 'name',
  emailDate: 'email_date',
  unsubscribeUrl: 'unsubscribe_url',
  privacyPolicyUrl: 'privacy_policy_url',
  termsUrl: 'terms_url',
  
  // Registration email
  verificationLink: 'verification_link',
  
  // Password reset email
  resetLink: 'reset_link',
  
  // Notification email
  eventCount: 'event_count',
  recommendedCount: 'recommended_count',
  eventList: 'event_list',
  
  // Report email
  eventName: 'event_name',
  eventDate: 'event_date',
  eventVenue: 'event_venue',
  participantCount: 'participant_count',
  viewCount: 'view_count',
  clickThroughRate: 'click_through_rate',
  reportLink: 'report_link',
};

/**
 * Converts MediHub email templates for SendGrid with proper variable syntax
 * @param templateName - Name of the template to convert
 * @returns HTML with SendGrid-compatible variables
 */
export const getTemplateForSendGrid = (templateName: 'user-registration' | 'password-reset' | 'notification' | 'report-sending'): string => {
  let template = '';
  
  switch (templateName) {
    case 'user-registration':
      template = userRegistrationTemplate;
      break;
    case 'password-reset':
      template = passwordResetTemplate;
      break;
    case 'notification':
      template = notificationTemplate;
      break;
    case 'report-sending':
      template = reportSendingTemplate;
      break;
  }
  
  // Convert {{variable}} to SendGrid variable format if needed
  // By default we keep the Handlebars syntax as it's compatible with SendGrid
  
  return template;
};

/**
 * Creates a documentation object for PHP implementation
 * @param templateName - Name of the template
 * @returns Object with template info and required variables
 */
export const getTemplateDocumentation = (templateName: 'user-registration' | 'password-reset' | 'notification' | 'report-sending'): {
  description: string;
  requiredVariables: string[];
  optionalVariables: string[];
  samplePhpCode: string;
} => {
  switch (templateName) {
    case 'user-registration':
      return {
        description: 'Üdvözlő email új felhasználók regisztrációjakor',
        requiredVariables: ['name', 'verificationLink', 'privacyPolicyUrl', 'termsUrl', 'unsubscribeUrl'],
        optionalVariables: [],
        samplePhpCode: `
$sendgrid = new \\SendGrid\\Mail\\Mail();
$sendgrid->setFrom("no-reply@medihub.hu", "MediHub");
$sendgrid->setSubject("Üdvözöljük a MediHub rendszerében!");
$sendgrid->addTo($userEmail, $userName);
$sendgrid->setTemplateId("d-xxxxxxxxxxxx"); // SendGrid template ID

// Template data
$sendgrid->addDynamicTemplateData("name", $userName);
$sendgrid->addDynamicTemplateData("verification_link", $verificationLink);
$sendgrid->addDynamicTemplateData("privacy_policy_url", "https://medihub.hu/privacy-policy");
$sendgrid->addDynamicTemplateData("terms_url", "https://medihub.hu/terms");
$sendgrid->addDynamicTemplateData("unsubscribe_url", $unsubscribeUrl);

$sg = new \\SendGrid($sendgridApiKey);
$response = $sg->send($sendgrid);
`
      };
    
    case 'password-reset':
      return {
        description: 'Jelszó visszaállítási email',
        requiredVariables: ['name', 'resetLink', 'privacyPolicyUrl', 'termsUrl'],
        optionalVariables: [],
        samplePhpCode: `
$sendgrid = new \\SendGrid\\Mail\\Mail();
$sendgrid->setFrom("no-reply@medihub.hu", "MediHub");
$sendgrid->setSubject("Jelszó visszaállítás - MediHub");
$sendgrid->addTo($userEmail, $userName);
$sendgrid->setTemplateId("d-xxxxxxxxxxxx"); // SendGrid template ID

// Template data
$sendgrid->addDynamicTemplateData("name", $userName);
$sendgrid->addDynamicTemplateData("reset_link", $resetLink);
$sendgrid->addDynamicTemplateData("privacy_policy_url", "https://medihub.hu/privacy-policy");
$sendgrid->addDynamicTemplateData("terms_url", "https://medihub.hu/terms");

$sg = new \\SendGrid($sendgridApiKey);
$response = $sg->send($sendgrid);
`
      };
    
    case 'notification':
      return {
        description: 'Értesítő email eseményekről',
        requiredVariables: ['name', 'emailDate', 'eventCount', 'recommendedCount', 'unsubscribeUrl', 'privacyPolicyUrl', 'termsUrl'],
        optionalVariables: ['eventList'],
        samplePhpCode: `
$sendgrid = new \\SendGrid\\Mail\\Mail();
$sendgrid->setFrom("no-reply@medihub.hu", "MediHub");
$sendgrid->setSubject("Új események a MediHub-on");
$sendgrid->addTo($userEmail, $userName);
$sendgrid->setTemplateId("d-xxxxxxxxxxxx"); // SendGrid template ID

// Template data
$sendgrid->addDynamicTemplateData("name", $userName);
$sendgrid->addDynamicTemplateData("email_date", date("Y. F j."));
$sendgrid->addDynamicTemplateData("event_count", count($events));
$sendgrid->addDynamicTemplateData("recommended_count", count($recommendedEvents));
$sendgrid->addDynamicTemplateData("unsubscribe_url", $unsubscribeUrl);
$sendgrid->addDynamicTemplateData("privacy_policy_url", "https://medihub.hu/privacy-policy");
$sendgrid->addDynamicTemplateData("terms_url", "https://medihub.hu/terms");

// Generate event list HTML
$eventListHtml = '';
foreach ($events as $event) {
    $eventListHtml .= "<div class='event-item'>";
    $eventListHtml .= "<h3>{$event['name']}</h3>";
    $eventListHtml .= "<p>Típus: {$event['type']}</p>";
    $eventListHtml .= "<p>Időpont: {$event['date']}</p>";
    $eventListHtml .= "<p>Helyszín: {$event['location']}</p>";
    $eventListHtml .= "<a href='{$event['link']}'>Részletek</a>";
    $eventListHtml .= "</div>";
}
$sendgrid->addDynamicTemplateData("event_list", $eventListHtml);

$sg = new \\SendGrid($sendgridApiKey);
$response = $sg->send($sendgrid);
`
      };
    
    case 'report-sending':
      return {
        description: 'Esemény jelentés küldése',
        requiredVariables: ['name', 'eventName', 'eventDate', 'eventVenue', 'participantCount', 'viewCount', 'clickThroughRate', 'reportLink', 'unsubscribeUrl', 'privacyPolicyUrl', 'termsUrl'],
        optionalVariables: [],
        samplePhpCode: `
$sendgrid = new \\SendGrid\\Mail\\Mail();
$sendgrid->setFrom("no-reply@medihub.hu", "MediHub");
$sendgrid->setSubject("Esemény jelentés - {$eventName}");
$sendgrid->addTo($userEmail, $userName);
$sendgrid->setTemplateId("d-xxxxxxxxxxxx"); // SendGrid template ID

// Template data
$sendgrid->addDynamicTemplateData("name", $userName);
$sendgrid->addDynamicTemplateData("event_name", $eventName);
$sendgrid->addDynamicTemplateData("event_date", $eventDate);
$sendgrid->addDynamicTemplateData("event_venue", $eventVenue);
$sendgrid->addDynamicTemplateData("participant_count", $participantCount);
$sendgrid->addDynamicTemplateData("view_count", $viewCount);
$sendgrid->addDynamicTemplateData("click_through_rate", $clickThroughRate);
$sendgrid->addDynamicTemplateData("report_link", $reportLink);
$sendgrid->addDynamicTemplateData("unsubscribe_url", $unsubscribeUrl);
$sendgrid->addDynamicTemplateData("privacy_policy_url", "https://medihub.hu/privacy-policy");
$sendgrid->addDynamicTemplateData("terms_url", "https://medihub.hu/terms");

$sg = new \\SendGrid($sendgridApiKey);
$response = $sg->send($sendgrid);
`
      };
    
    default:
      return {
        description: '',
        requiredVariables: [],
        optionalVariables: [],
        samplePhpCode: ''
      };
  }
};
